import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Stack,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  Divider,
} from '@mui/material';
import {
  Search,
  Edit,
  CheckCircle,
  Block,
  Percent,
  Store,
  People,
  PendingActions,
  AccountBalance,
  Visibility,
  Phone,
  Email,
  Language,
  Instagram,
} from '@mui/icons-material';
import { supabase } from '../../supabaseClient';
import { useTranslation } from 'react-i18next';

interface SellerProfile {
  id: string;
  user_id: string;
  store_name: string;
  store_description: string | null;
  business_number: string;
  business_type: string;
  owner_name: string;
  address: string | null;
  contact_email: string;
  contact_phone: string | null;
  bank_name: string;
  bank_account: string;
  bank_account_holder: string;
  status: string;
  joined_at: string;
  last_active_at: string | null;
  commission_rate: number;
  payment_fee_rate: number;
  profile_image: string | null;
  banner_image: string | null;
  bio: string | null;
  social_links: {
    instagram?: string;
    website?: string;
    tiktok?: string;
    youtube?: string;
  } | null;
}

interface SellerStore {
  id: string;
  seller_id: string;
  store_name: string;
  store_description: string;
  logo_image: string | null;
}

export default function SellerManagement() {
  const { t } = useTranslation();
  const [sellers, setSellers] = useState<SellerProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedSeller, setSelectedSeller] = useState<SellerProfile | null>(null);
  const [selectedSellerStore, setSelectedSellerStore] = useState<SellerStore | null>(null);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [commissionDialogOpen, setCommissionDialogOpen] = useState(false);
  const [paymentFeeDialogOpen, setPaymentFeeDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [newCommissionRate, setNewCommissionRate] = useState('');
  const [newPaymentFeeRate, setNewPaymentFeeRate] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    loadSellers();
  }, []);

  const loadSellers = async () => {
    try {
      setLoading(true);

      const { data, error } = await supabase
        .from('seller_profile')
        .select('*')
        .order('joined_at', { ascending: false });

      if (error) {
        console.error('Error loading sellers:', error);
        return;
      }

      setSellers(data || []);
    } catch (error) {
      console.error('Error in loadSellers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenStatusDialog = (seller: SellerProfile) => {
    setSelectedSeller(seller);
    setNewStatus(seller.status);
    setStatusDialogOpen(true);
  };

  const handleOpenCommissionDialog = (seller: SellerProfile) => {
    setSelectedSeller(seller);
    setNewCommissionRate(seller.commission_rate.toString());
    setCommissionDialogOpen(true);
  };

  const handleOpenPaymentFeeDialog = (seller: SellerProfile) => {
    setSelectedSeller(seller);
    setNewPaymentFeeRate(seller.payment_fee_rate?.toString() || '3.6');
    setPaymentFeeDialogOpen(true);
  };

  const handleOpenDetailDialog = async (seller: SellerProfile) => {
    setSelectedSeller(seller);
    setSelectedSellerStore(null);

    // Seller Store 정보 조회
    try {
      const { data: storeData, error } = await supabase
        .from('seller_store')
        .select('*')
        .eq('seller_id', seller.id)
        .single();

      if (!error && storeData) {
        setSelectedSellerStore(storeData);
      }
    } catch (error) {
      console.error('Error loading seller store:', error);
    }

    setDetailDialogOpen(true);
  };

  const handleApproveSeller = async (seller: SellerProfile) => {
    if (!window.confirm(t('sellerManagement.confirmApprove', { name: seller.store_name }))) {
      return;
    }

    try {
      // seller_profile 상태를 active로 업데이트
      const { error: statusError } = await supabase
        .from('seller_profile')
        .update({ status: 'active' })
        .eq('id', seller.id);

      if (statusError) {
        console.error('Error updating seller status:', statusError);
        alert(t('sellerManagement.approveError'));
        return;
      }

      // profiles 테이블의 role을 'seller'로 업데이트
      const { error: roleError } = await supabase
        .from('profiles')
        .update({ role: 'seller' })
        .eq('id', seller.user_id);

      if (roleError) {
        console.error('Error updating profile role:', roleError);
        alert(t('sellerManagement.roleUpdateError'));
        return;
      }

      alert(t('sellerManagement.approveSuccess', { name: seller.store_name }));
      loadSellers();
    } catch (error) {
      console.error('Error in handleApproveSeller:', error);
      alert(t('sellerManagement.approveException'));
    }
  };

  const handleUpdateStatus = async () => {
    if (!selectedSeller) return;

    try {
      // seller_profile 상태 업데이트
      const { error } = await supabase
        .from('seller_profile')
        .update({ status: newStatus })
        .eq('id', selectedSeller.id);

      if (error) {
        console.error('Error updating status:', error);
        alert(t('sellerManagement.statusUpdateError'));
        return;
      }

      // pending -> active로 변경 시 profiles 테이블의 role도 'seller'로 업데이트
      if (selectedSeller.status !== 'active' && newStatus === 'active') {
        const { error: roleError } = await supabase
          .from('profiles')
          .update({ role: 'seller' })
          .eq('id', selectedSeller.user_id);

        if (roleError) {
          console.error('Error updating profile role:', roleError);
          alert(t('sellerManagement.roleUpdateError'));
          return;
        }
      }

      // active -> 다른 상태로 변경 시 profiles 테이블의 role을 'user'로 되돌림
      if (selectedSeller.status === 'active' && newStatus !== 'active') {
        const { error: roleError } = await supabase
          .from('profiles')
          .update({ role: 'user' })
          .eq('id', selectedSeller.user_id);

        if (roleError) {
          console.error('Error updating profile role:', roleError);
        }
      }

      alert(t('sellerManagement.statusUpdateSuccess', { name: selectedSeller.store_name, status: getStatusText(newStatus) }));
      setStatusDialogOpen(false);
      loadSellers();
    } catch (error) {
      console.error('Error in handleUpdateStatus:', error);
      alert(t('sellerManagement.statusUpdateException'));
    }
  };

  const handleUpdateCommissionRate = async () => {
    if (!selectedSeller) return;

    const rate = parseFloat(newCommissionRate);
    if (isNaN(rate) || rate < 0 || rate > 100) {
      alert(t('sellerManagement.commissionRangeError'));
      return;
    }

    try {
      const { error } = await supabase
        .from('seller_profile')
        .update({ commission_rate: rate })
        .eq('id', selectedSeller.id);

      if (error) {
        console.error('Error updating commission rate:', error);
        alert(t('sellerManagement.commissionUpdateError'));
        return;
      }

      alert(t('sellerManagement.commissionUpdateSuccess', { name: selectedSeller.store_name, rate }));
      setCommissionDialogOpen(false);
      loadSellers();
    } catch (error) {
      console.error('Error in handleUpdateCommissionRate:', error);
      alert(t('sellerManagement.commissionUpdateException'));
    }
  };

  const handleUpdatePaymentFeeRate = async () => {
    if (!selectedSeller) return;

    const rate = parseFloat(newPaymentFeeRate);
    if (isNaN(rate) || rate < 0 || rate > 100) {
      alert(t('sellerManagement.paymentFeeRangeError'));
      return;
    }

    try {
      const { error } = await supabase
        .from('seller_profile')
        .update({ payment_fee_rate: rate })
        .eq('id', selectedSeller.id);

      if (error) {
        console.error('Error updating payment fee rate:', error);
        alert(t('sellerManagement.paymentFeeUpdateError'));
        return;
      }

      alert(t('sellerManagement.paymentFeeUpdateSuccess', { name: selectedSeller.store_name, rate }));
      setPaymentFeeDialogOpen(false);
      loadSellers();
    } catch (error) {
      console.error('Error in handleUpdatePaymentFeeRate:', error);
      alert(t('sellerManagement.paymentFeeUpdateException'));
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'success';
      case 'pending': return 'warning';
      case 'suspended': return 'error';
      case 'rejected': return 'default';
      default: return 'default';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'active': return t('sellerManagement.activeStatus');
      case 'pending': return t('sellerManagement.pendingStatus');
      case 'suspended': return t('sellerManagement.suspendedStatus');
      case 'rejected': return t('sellerManagement.rejectedStatus');
      default: return status;
    }
  };

  const filteredSellers = sellers.filter(seller => {
    const matchesSearch = seller.store_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         seller.owner_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         seller.business_number.includes(searchTerm);
    const matchesStatus = statusFilter === 'all' || seller.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const paginatedSellers = filteredSellers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // 통계 계산
  const totalSellers = sellers.length;
  const activeSellers = sellers.filter(s => s.status === 'active').length;
  const pendingSellers = sellers.filter(s => s.status === 'pending').length;
  const avgCommissionRate = sellers.length > 0
    ? sellers.reduce((sum, s) => sum + s.commission_rate, 0) / sellers.length
    : 0;

  return (
    <Box sx={{ p: 3, backgroundColor: '#f8fafc', minHeight: '100vh' }}>
      <Typography variant="h4" sx={{ fontWeight: 600, mb: 3, color: '#1f2937' }}>
        {t('sellerManagement.title')}
      </Typography>

      {/* 통계 카드 */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Box sx={{ p: 1.5, borderRadius: 1.5, backgroundColor: '#dbeafe' }}>
                  <People sx={{ color: '#3b82f6' }} />
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    {t('sellerManagement.totalSellersCard')}
                  </Typography>
                  <Typography variant="h5" fontWeight={700} color="primary">
                    {totalSellers}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Box sx={{ p: 1.5, borderRadius: 1.5, backgroundColor: '#d1fae5' }}>
                  <CheckCircle sx={{ color: '#10b981' }} />
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    {t('sellerManagement.activeSellersCard')}
                  </Typography>
                  <Typography variant="h5" fontWeight={700} color="success.main">
                    {activeSellers}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Box sx={{ p: 1.5, borderRadius: 1.5, backgroundColor: '#fef3c7' }}>
                  <PendingActions sx={{ color: '#f59e0b' }} />
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    {t('sellerManagement.pendingSellersCard')}
                  </Typography>
                  <Typography variant="h5" fontWeight={700} color="warning.main">
                    {pendingSellers}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Box sx={{ p: 1.5, borderRadius: 1.5, backgroundColor: '#e0e7ff' }}>
                  <Percent sx={{ color: '#6366f1' }} />
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    {t('sellerManagement.avgCommissionRate')}
                  </Typography>
                  <Typography variant="h5" fontWeight={700} color="#6366f1">
                    {avgCommissionRate.toFixed(2)}%
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* 필터 및 검색 */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              placeholder={t('sellerManagement.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>{t('sellerManagement.statusLabel')}</InputLabel>
              <Select
                value={statusFilter}
                label={t('sellerManagement.statusLabel')}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">{t('sellerManagement.allStatus')}</MenuItem>
                <MenuItem value="active">{t('sellerManagement.activeStatus')}</MenuItem>
                <MenuItem value="pending">{t('sellerManagement.pendingStatus')}</MenuItem>
                <MenuItem value="suspended">{t('sellerManagement.suspendedStatus')}</MenuItem>
                <MenuItem value="rejected">{t('sellerManagement.rejectedStatus')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* 셀러 테이블 */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('sellerManagement.storeNameLabel')}</TableCell>
                <TableCell>{t('sellerManagement.ownerName')}</TableCell>
                <TableCell>{t('sellerManagement.businessNumber')}</TableCell>
                <TableCell>{t('sellerManagement.contact')}</TableCell>
                <TableCell>{t('sellerManagement.platformFee')}</TableCell>
                <TableCell>{t('sellerManagement.paymentFee')}</TableCell>
                <TableCell>{t('sellerManagement.statusLabel')}</TableCell>
                <TableCell>{t('sellerManagement.joinedAt')}</TableCell>
                <TableCell align="center">{t('sellerManagement.manage')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    <Typography>{t('sellerManagement.loadingData')}</Typography>
                  </TableCell>
                </TableRow>
              ) : paginatedSellers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    <Typography>{t('sellerManagement.noSellersFound')}</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedSellers.map((seller) => (
                  <TableRow key={seller.id} hover>
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Store fontSize="small" color="action" />
                        <Typography variant="body2" fontWeight="bold">
                          {seller.store_name}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>{seller.owner_name}</TableCell>
                    <TableCell>{seller.business_number}</TableCell>
                    <TableCell>
                      <Typography variant="body2">{seller.contact_email}</Typography>
                      {seller.contact_phone && (
                        <Typography variant="caption" color="text.secondary">
                          {seller.contact_phone}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={`${seller.commission_rate}%`}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={`${seller.payment_fee_rate || 3.6}%`}
                        size="small"
                        color="secondary"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getStatusText(seller.status)}
                        color={getStatusColor(seller.status) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {new Date(seller.joined_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell align="center">
                      <Stack direction="row" spacing={1} justifyContent="center">
                        <Tooltip title={t('sellerManagement.viewDetailTooltip')}>
                          <IconButton
                            size="small"
                            onClick={() => handleOpenDetailDialog(seller)}
                          >
                            <Visibility fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        {seller.status === 'pending' ? (
                          // pending 상태: 승인 버튼만 표시
                          <Tooltip title={t('sellerManagement.approveTooltip')}>
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() => handleApproveSeller(seller)}
                            >
                              <CheckCircle fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          // active 또는 기타 상태: 3개 관리 버튼 표시
                          <>
                            <Tooltip title={t('sellerManagement.statusChangeTooltip')}>
                              <IconButton
                                size="small"
                                onClick={() => handleOpenStatusDialog(seller)}
                              >
                                <Edit fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            {seller.status === 'active' && (
                              <>
                                <Tooltip title={t('sellerManagement.platformFeeTooltip')}>
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={() => handleOpenCommissionDialog(seller)}
                                  >
                                    <Percent fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title={t('sellerManagement.paymentFeeTooltip')}>
                                  <IconButton
                                    size="small"
                                    color="secondary"
                                    onClick={() => handleOpenPaymentFeeDialog(seller)}
                                  >
                                    <AccountBalance fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </>
                            )}
                          </>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={filteredSellers.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
          labelRowsPerPage={t('sellerManagement.rowsPerPage')}
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count}`}
        />
      </Paper>

      {/* 상태 변경 다이얼로그 */}
      <Dialog
        open={statusDialogOpen}
        onClose={() => setStatusDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('sellerManagement.statusChangeDialog')}</DialogTitle>
        <DialogContent>
          {selectedSeller && (
            <Box sx={{ pt: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                <strong>{selectedSeller.store_name}</strong> {t('sellerManagement.statusChangeDesc', { name: '' }).replace('{{name}}', '').trim()}
              </Typography>
              <FormControl fullWidth>
                <InputLabel>{t('sellerManagement.statusLabel')}</InputLabel>
                <Select
                  value={newStatus}
                  label={t('sellerManagement.statusLabel')}
                  onChange={(e) => setNewStatus(e.target.value)}
                >
                  <MenuItem value="active">{t('sellerManagement.activeStatus')}</MenuItem>
                  <MenuItem value="pending">{t('sellerManagement.pendingStatus')}</MenuItem>
                  <MenuItem value="suspended">{t('sellerManagement.suspendedStatus')}</MenuItem>
                  <MenuItem value="rejected">{t('sellerManagement.rejectedStatus')}</MenuItem>
                </Select>
              </FormControl>
              <Box sx={{ mt: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  {t('sellerManagement.currentStatus', { status: getStatusText(selectedSeller.status) })}
                </Typography>
                <br />
                <Typography variant="caption" color="text.secondary">
                  {t('sellerManagement.newStatus', { status: getStatusText(newStatus) })}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusDialogOpen(false)}>{t('common.cancel')}</Button>
          <Button variant="contained" onClick={handleUpdateStatus} color="primary">
            {t('sellerManagement.changeButton')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 수수료율 변경 다이얼로그 */}
      <Dialog
        open={commissionDialogOpen}
        onClose={() => setCommissionDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('sellerManagement.commissionDialog')}</DialogTitle>
        <DialogContent>
          {selectedSeller && (
            <Box sx={{ pt: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t('sellerManagement.commissionDialogDesc', { name: selectedSeller.store_name })}
              </Typography>
              <Typography variant="caption" color="warning.main" sx={{ mb: 3, display: 'block' }}>
                {t('sellerManagement.commissionWarning')}
              </Typography>
              <TextField
                fullWidth
                label={t('sellerManagement.commissionRateLabel')}
                type="number"
                value={newCommissionRate}
                onChange={(e) => setNewCommissionRate(e.target.value)}
                InputProps={{
                  endAdornment: <InputAdornment position="end">%</InputAdornment>,
                }}
                inputProps={{
                  min: 0,
                  max: 100,
                  step: 0.01
                }}
                helperText={t('sellerManagement.commissionHelper')}
                sx={{ mt: 2 }}
              />
              <Box sx={{ mt: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  {t('sellerManagement.currentCommissionRate', { rate: selectedSeller.commission_rate })}
                </Typography>
                <br />
                <Typography variant="caption" color="text.secondary">
                  {t('sellerManagement.newCommissionRate', { rate: newCommissionRate || '0' })}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCommissionDialogOpen(false)}>{t('common.cancel')}</Button>
          <Button variant="contained" onClick={handleUpdateCommissionRate} color="primary">
            {t('sellerManagement.changeButton')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 결제 수수료율 변경 다이얼로그 */}
      <Dialog
        open={paymentFeeDialogOpen}
        onClose={() => setPaymentFeeDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('sellerManagement.paymentFeeDialog')}</DialogTitle>
        <DialogContent>
          {selectedSeller && (
            <Box sx={{ pt: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t('sellerManagement.paymentFeeDialogDesc', { name: selectedSeller.store_name })}
              </Typography>
              <Typography variant="caption" color="warning.main" sx={{ mb: 3, display: 'block' }}>
                {t('sellerManagement.paymentFeeWarning')}
              </Typography>
              <TextField
                fullWidth
                label={t('sellerManagement.paymentFeeRateLabel')}
                type="number"
                value={newPaymentFeeRate}
                onChange={(e) => setNewPaymentFeeRate(e.target.value)}
                InputProps={{
                  endAdornment: <InputAdornment position="end">%</InputAdornment>,
                }}
                inputProps={{
                  min: 0,
                  max: 100,
                  step: 0.01
                }}
                helperText={t('sellerManagement.paymentFeeHelper')}
                sx={{ mt: 2 }}
              />
              <Box sx={{ mt: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  {t('sellerManagement.currentPaymentFeeRate', { rate: selectedSeller.payment_fee_rate || 3.6 })}
                </Typography>
                <br />
                <Typography variant="caption" color="text.secondary">
                  {t('sellerManagement.newPaymentFeeRate', { rate: newPaymentFeeRate || '0' })}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPaymentFeeDialogOpen(false)}>{t('common.cancel')}</Button>
          <Button variant="contained" onClick={handleUpdatePaymentFeeRate} color="secondary">
            {t('sellerManagement.changeButton')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 셀러 상세 정보 다이얼로그 */}
      <Dialog
        open={detailDialogOpen}
        onClose={() => setDetailDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{t('sellerManagement.detailDialog')}</DialogTitle>
        <DialogContent>
          {selectedSeller && (
            <Box sx={{ pt: 2 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                {t('sellerManagement.basicInfo')}
              </Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">{t('sellerManagement.storeNameLabel')}</Typography>
                  <Typography variant="body1">{selectedSeller.store_name}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">{t('sellerManagement.businessNumberLabel')}</Typography>
                  <Typography variant="body1">{selectedSeller.business_number}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">{t('sellerManagement.businessTypeLabel')}</Typography>
                  <Typography variant="body1">
                    {selectedSeller.business_type === 'individual' ? t('sellerManagement.individual') : t('sellerManagement.corporation')}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">{t('sellerManagement.ownerNameLabel')}</Typography>
                  <Typography variant="body1">{selectedSeller.owner_name}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">{t('sellerManagement.statusInfoLabel')}</Typography>
                  <Chip
                    label={getStatusText(selectedSeller.status)}
                    color={getStatusColor(selectedSeller.status) as any}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">{t('sellerManagement.joinedAtLabel')}</Typography>
                  <Typography variant="body1">
                    {new Date(selectedSeller.joined_at).toLocaleString('ko-KR')}
                  </Typography>
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                {t('sellerManagement.contactInfo')}
              </Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Email fontSize="small" color="action" />
                    <Box>
                      <Typography variant="caption" color="text.secondary">{t('sellerManagement.emailLabel')}</Typography>
                      <Typography variant="body1">{selectedSeller.contact_email}</Typography>
                    </Box>
                  </Stack>
                </Grid>
                <Grid item xs={12}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Phone fontSize="small" color="action" />
                    <Box>
                      <Typography variant="caption" color="text.secondary">{t('sellerManagement.phoneLabel')}</Typography>
                      <Typography variant="body1">{selectedSeller.contact_phone || '-'}</Typography>
                    </Box>
                  </Stack>
                </Grid>
                <Grid item xs={12}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Store fontSize="small" color="action" />
                    <Box>
                      <Typography variant="caption" color="text.secondary">{t('sellerManagement.addressLabel')}</Typography>
                      <Typography variant="body1">{selectedSeller.address || '-'}</Typography>
                    </Box>
                  </Stack>
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                {t('sellerManagement.settlementInfo')}
              </Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">{t('sellerManagement.bankNameLabel')}</Typography>
                  <Typography variant="body1">{selectedSeller.bank_name}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">{t('sellerManagement.bankAccountLabel')}</Typography>
                  <Typography variant="body1">{selectedSeller.bank_account}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">{t('sellerManagement.bankAccountHolderLabel')}</Typography>
                  <Typography variant="body1">{selectedSeller.bank_account_holder}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">{t('sellerManagement.platformFeeRateLabel')}</Typography>
                  <Typography variant="body1">{selectedSeller.commission_rate}%</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">{t('sellerManagement.paymentFeeRateLabel')}</Typography>
                  <Typography variant="body1">{selectedSeller.payment_fee_rate || 3.6}%</Typography>
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                {t('sellerManagement.profileInfo')}
              </Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">{t('sellerManagement.bio')}</Typography>
                  <Typography variant="body1">{selectedSeller.bio || '-'}</Typography>
                </Grid>
                {selectedSeller.social_links && (
                  <>
                    {selectedSeller.social_links.website && (
                      <Grid item xs={12} md={6}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Language fontSize="small" color="action" />
                          <Box>
                            <Typography variant="caption" color="text.secondary">{t('sellerManagement.website')}</Typography>
                            <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                              <a href={selectedSeller.social_links.website} target="_blank" rel="noopener noreferrer">
                                {selectedSeller.social_links.website}
                              </a>
                            </Typography>
                          </Box>
                        </Stack>
                      </Grid>
                    )}
                    {selectedSeller.social_links.instagram && (
                      <Grid item xs={12} md={6}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Instagram fontSize="small" color="action" />
                          <Box>
                            <Typography variant="caption" color="text.secondary">{t('sellerManagement.instagram')}</Typography>
                            <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                              <a href={selectedSeller.social_links.instagram} target="_blank" rel="noopener noreferrer">
                                {selectedSeller.social_links.instagram}
                              </a>
                            </Typography>
                          </Box>
                        </Stack>
                      </Grid>
                    )}
                    {selectedSeller.social_links.tiktok && (
                      <Grid item xs={12} md={6}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Language fontSize="small" color="action" />
                          <Box>
                            <Typography variant="caption" color="text.secondary">TikTok</Typography>
                            <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                              <a href={selectedSeller.social_links.tiktok} target="_blank" rel="noopener noreferrer">
                                {selectedSeller.social_links.tiktok}
                              </a>
                            </Typography>
                          </Box>
                        </Stack>
                      </Grid>
                    )}
                    {selectedSeller.social_links.youtube && (
                      <Grid item xs={12} md={6}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Language fontSize="small" color="action" />
                          <Box>
                            <Typography variant="caption" color="text.secondary">YouTube</Typography>
                            <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                              <a href={selectedSeller.social_links.youtube} target="_blank" rel="noopener noreferrer">
                                {selectedSeller.social_links.youtube}
                              </a>
                            </Typography>
                          </Box>
                        </Stack>
                      </Grid>
                    )}
                  </>
                )}
              </Grid>

              {selectedSellerStore && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                    {t('sellerManagement.storeInfo')}
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary">{t('sellerManagement.storeDescription')}</Typography>
                      <Typography variant="body1">{selectedSellerStore.store_description || '-'}</Typography>
                    </Grid>
                    {selectedSellerStore.logo_image && (
                      <Grid item xs={12}>
                        <Typography variant="caption" color="text.secondary">{t('sellerManagement.logoImage')}</Typography>
                        <Box sx={{ mt: 1 }}>
                          <img
                            src={selectedSellerStore.logo_image}
                            alt="Store Logo"
                            style={{ maxWidth: '200px', maxHeight: '100px', objectFit: 'contain' }}
                          />
                        </Box>
                      </Grid>
                    )}
                  </Grid>
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialogOpen(false)}>{t('common.close')}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
